
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Database, Home, Bell, Search, 
  Settings, User, Shield, 
  CheckSquare, FileText, Receipt, 
  ChartBar, IndianRupee, MessageSquare
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AICopilot from './AICopilot';

interface SidebarItemProps {
  to: string;
  icon: React.ElementType;
  label: string;
  isCollapsed: boolean;
  children?: React.ReactNode;
}

const SidebarItem = ({ to, icon: Icon, label, isCollapsed, children }: SidebarItemProps) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  const isChildActive = location.pathname.startsWith(to) && location.pathname !== to;
  const [isOpen, setIsOpen] = useState(isChildActive);
  
  return (
    <div className="w-full">
      <Link 
        to={to} 
        className={cn(
          "sidebar-item",
          isActive && "active"
        )}
        onClick={() => children && setIsOpen(!isOpen)}
      >
        <Icon size={20} />
        {!isCollapsed && <span>{label}</span>}
        {!isCollapsed && children && (
          <span className="ml-auto">{isOpen ? '−' : '+'}</span>
        )}
      </Link>
      
      {!isCollapsed && children && isOpen && (
        <div className="pl-8 mt-1 space-y-1">
          {children}
        </div>
      )}
    </div>
  );
};

interface NestedLinkProps {
  to: string;
  label: string;
}

const NestedLink = ({ to, label }: NestedLinkProps) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Link 
      to={to} 
      className={cn(
        "flex items-center px-2 py-1.5 text-sm rounded-md text-white hover:bg-sidebar-accent/30 hover:text-sidebar-accent transition-colors",
        isActive && "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
      )}
    >
      {label}
    </Link>
  );
};

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showAICopilot, setShowAICopilot] = useState(false);

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside 
        className={cn(
          "bg-sidebar flex flex-col border-r border-sidebar-border transition-all duration-300",
          isCollapsed ? "w-[70px]" : "w-[250px]"
        )}
      >
        {/* Logo area */}
        <div className="p-4 border-b border-sidebar-border flex items-center justify-between">
          {!isCollapsed ? (
            <div className="flex items-center">
              <img 
                src="/lovable-uploads/c1a1fcda-813b-4d91-8905-237024aa3d9b.png" 
                alt="Rench Logo" 
                className="h-8 w-auto" 
                loading="lazy" 
                width="32"
                height="32"
              />
            </div>
          ) : (
            <div className="w-8 h-8">
              {/* Empty div to maintain spacing */}
            </div>
          )}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-sidebar-foreground hover:text-sidebar-accent"
          >
            {isCollapsed ? <span>→</span> : <span>←</span>}
          </Button>
        </div>
        
        {/* Navigation - Updated order with Accounting Operations and Compliance swapped */}
        <div className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          <SidebarItem to="/" icon={Home} label="Dashboard" isCollapsed={isCollapsed} />
          <SidebarItem to="/borrowings" icon={FileText} label="Debt Capital" isCollapsed={isCollapsed} />
          <SidebarItem to="/alm" icon={IndianRupee} label="ALM & Cash Position" isCollapsed={isCollapsed}>
            <NestedLink to="/alm/investments" label="Investments" />
            <NestedLink to="/alm/borrowings" label="Borrowings" />
          </SidebarItem>
          <SidebarItem to="/accounting" icon={ChartBar} label="Accounting Operations" isCollapsed={isCollapsed} />
          <SidebarItem to="/compliance" icon={Shield} label="Compliance" isCollapsed={isCollapsed}>
            <NestedLink to="/compliance/repository" label="Compliance Repository" />
            <NestedLink to="/compliance/reports" label="Compliance Reports" />
            <NestedLink to="/compliance/audit-triggers" label="Predictive Audit Triggers" />
            <NestedLink to="/compliance/secretarial" label="Secretarial Compliance" />
          </SidebarItem>
          <SidebarItem to="/tasks" icon={CheckSquare} label="Tasks" isCollapsed={isCollapsed} />
          <SidebarItem to="/documents" icon={FileText} label="Documents Hub" isCollapsed={isCollapsed} />
          <SidebarItem to="/datahub" icon={Database} label="Data Hub" isCollapsed={isCollapsed} />
          <SidebarItem to="/settings" icon={Settings} label="Settings" isCollapsed={isCollapsed} />
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top navigation */}
        <header className="bg-card border-b h-16 px-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Search removed */}
          </div>
          
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => setShowAICopilot(!showAICopilot)}
            >
              <MessageSquare size={20} />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-rench-green text-xs flex items-center justify-center rounded-full text-black font-medium">AI</span>
            </Button>
            <Button variant="ghost" size="icon" className="relative">
              <Bell size={20} />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-rench-green text-xs flex items-center justify-center rounded-full text-black font-medium">3</span>
            </Button>
            <Link to="/settings">
              <Button variant="ghost" size="icon">
                <Settings size={20} />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="bg-muted rounded-full">
                <User size={20} />
              </Button>
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main className="flex-1 overflow-auto p-6 bg-background">
          {children}
        </main>
        
        {/* AI Copilot */}
        {showAICopilot && (
          <AICopilot onClose={() => setShowAICopilot(false)} />
        )}
      </div>
    </div>
  );
};

export default Layout;
